import { DateTime } from 'luxon';

import type { SvrContactField } from '@poseidon/types/svr/svrContact.type';

import { shouldShowHonorific } from './genderDetection';

export const isNotNull = <T>(arg: T | undefined | null): arg is T => arg != null;

export const isNotEmpty = (arg: string | undefined | null): arg is string => {
  return arg != null && arg.length > 0;
};

export const hasItems = <T>(arr: T[] | undefined | null): arr is [T, ...T[]] => arr != null && arr.length > 0;

export function extractAchronym(s: (string | undefined | null)[], maxLetters = 3) {
  const letters = s
    .filter((v) => v != null)
    .map((s) => s[0]?.toLocaleUpperCase())
    .join('');
  if (maxLetters) return letters.slice(0, maxLetters);
  else return letters;
}

export function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 6;
}

export const colors = [
  'linear-gradient(45deg, #42a11f, #2dca1f)',
  'linear-gradient(45deg, #73173f, #ca1f68)',
  'linear-gradient(45deg, #99aa38, #3e761a)',
  'linear-gradient(45deg, #25145e, #3910bc)',
  'linear-gradient(45deg, #621e76, #9f1fca)',
  'linear-gradient(45deg, #a92e2e, #ec2727)',
];

export const toRelativeWithToday = (date: Date) => {
  const dateTime = DateTime.fromJSDate(date);
  const isToday = dateTime.hasSame(DateTime.now(), 'day');

  if (isToday) return dateTime.toRelative();
  return dateTime.toLocaleString(DateTime.DATE_MED);
};

export const prettyDayFormat = (timeInMs: number) => {
  const msInHour = 60 * 60 * 1000;
  const msInDay = 24 * msInHour;

  if (timeInMs === 0) {
    return '0 days';
  } else if (timeInMs > msInDay) {
    return `${(timeInMs / msInDay).toFixed(1)} days`;
  } else {
    return `${(timeInMs / msInHour).toFixed(1)} hours`;
  }
};

const syllables = (x: string) => {
  /*
   * basic algorithm: each vowel-group indicates a syllable, except for: final
   * (silent) e 'ia' ind two syl @AddSyl and @SubSyl list regexps to massage the
   * basic count. Each match from @AddSyl adds 1 to the basic count, each
   * @SubSyl match -1 Keep in mind that when the regexps are checked, any final
   * 'e' will have been removed, and all '\'' will have been removed.
   */
  // cSpell:disable
  const subSyl = [/cial/, /tia/, /cius/, /cious/, /giu/, /ion/, /iou/, /sia$/, /.ely$/, /sed$/];

  const addSyl = [
    /ia/,
    /riet/,
    /dien/,
    /iu/,
    /io/,
    /ii/,
    /[aeiouym]bl$/,
    /[aeiou]{3}/,
    /^mc/,
    /ism$/,
    /([^aeiouy])\1l$/,
    /[^l]lien/,
    /^coa[dglx]./,
    /[^gq]ua[^auieo]/,
    /dnt$/,
  ];
  // cSpell:enable

  const xx = x.toLowerCase().replace(/'/g, '').replace(/e\b/g, '');
  const scrugg = xx.split(/[^aeiouy]+/).filter(Boolean);

  return undefined === x || null === x || '' === x
    ? 0
    : 1 === xx.length
      ? 1
      : subSyl.map((r) => (xx.match(r) || []).length).reduce((a, b) => a - b) +
        addSyl.map((r) => (xx.match(r) || []).length).reduce((a, b) => a + b) +
        scrugg.length -
        (scrugg.length > 0 && '' === scrugg[0] ? 1 : 0) +
        xx
          .split(/\b/)
          .map((x) => x.trim())
          .filter(Boolean)
          .filter((x) => !x.match(/[.,'!?]/g))
          .map((x) => (/[aeiouy]/.exec(x) ? 0 : 1) as number)
          .reduce((a, b) => {
            return a + b;
          });
};

const words = (x: string) => (x.split(/\s+/) || ['']).length;
const sentences = (x: string) => (x.split('. ') || ['']).length;
const syllablesPerWord = (x: string) => syllables(x) / words(x);
const wordsPerSentence = (x: string) => words(x) / sentences(x);

export const rate = (x: string) => 206.835 - 1.015 * wordsPerSentence(x) - 84.6 * syllablesPerWord(x);
export const grade = (x: string) => 0.39 * wordsPerSentence(x) + 11.8 * syllablesPerWord(x) - 15.59;

export function nullOnEmpty<S extends string | undefined | null>(str: S): S | null {
  if (str == null) return str;
  if (str.length <= 0) return null;
  return str;
}

export function undefinedOnEmpty<S extends string | undefined>(str: S): S | undefined {
  if (str == null) return str;
  if (str.length <= 0) return undefined;
  return str;
}

export type Value = undefined | string | number;

export function normalizeString(str: string) {
  return str.toLowerCase().replace(/\s|_|-/g, '');
}

/**
 * Will return normalized value
 *
 * Passed value: my_NAME iS-Ash
 *
 * Returned value: mynameisash
 */
export const normalizeValue = (value: Value | string[]) => {
  if (!value) {
    return value;
  } else if (Array.isArray(value)) {
    return value.map((el) => normalizeString(el));
  } else if (typeof value === 'number') {
    return normalizeString(String(value));
  }

  return normalizeString(value);
};

export type Header = {
  key: string;
  displayName: string;
  /** Extra accepted spellings for this column, matched case-insensitively and ignoring spaces/_/- (e.g. "linkedin" for the LinkedIn URL column). */
  aliases?: string[];
};

export const compareHeaderWithArray = (header: Header, arr?: Value[]) => {
  return arr?.find((value) => compareHeaderWithValue(header, value));
};

export const compareHeaderWithValue = (header: Header, value?: Value) => {
  const normalizedValue = normalizeValue(value);
  return (
    normalizeValue(header.displayName) === normalizedValue ||
    normalizeValue(header.key) === normalizedValue ||
    (header.aliases?.some((alias) => normalizeValue(alias) === normalizedValue) ?? false)
  );
};

// ---------------------------------------------------------------------------
// Fuzzy name matching
//
// Used to reconcile free-text campaign / owner values from a CSV against our
// canonical campaign names and team members, tolerating case, whitespace,
// diacritics, punctuation, typos, and word-order ("Last, First") differences.
// Pure + dependency-free so it runs in the browser (no `latinize`).
// ---------------------------------------------------------------------------

/** Lowercase + strip accents/diacritics (e.g. "José" → "jose"). Browser-safe. */
export function stripDiacritics(str: string): string {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

/** Collapse a name to its comparable core: lower, de-accented, no separators/punct. */
function normalizeForMatch(str: string): string {
  return stripDiacritics(str.toLowerCase()).replace(/[\s_\-.,'"()]+/g, '');
}

/** Sort whitespace/comma-separated tokens so "John Smith" and "Smith, John" align. */
function tokenSortNormalize(str: string): string {
  return stripDiacritics(str.toLowerCase())
    .split(/[\s_\-.,]+/)
    .filter(Boolean)
    .sort()
    .join('');
}

function bigramCounts(s: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (let i = 0; i < s.length - 1; i++) {
    const bg = s.slice(i, i + 2);
    counts.set(bg, (counts.get(bg) ?? 0) + 1);
  }
  return counts;
}

/** Sørensen–Dice coefficient over character bigrams, in [0, 1]. */
function diceCoefficient(a: string, b: string): number {
  if (a.length < 2 || b.length < 2) return a === b ? 1 : 0;
  const bgA = bigramCounts(a);
  const bgB = bigramCounts(b);
  let intersection = 0;
  for (const [bg, countA] of bgA) {
    const countB = bgB.get(bg);
    if (countB != null) intersection += Math.min(countA, countB);
  }
  return (2 * intersection) / (a.length - 1 + (b.length - 1));
}

/**
 * Similarity of two names in [0, 1]. Exact (after normalization) = 1; otherwise
 * the max of a direct bigram-Dice compare and a token-sorted compare (so word
 * order / "Last, First" doesn't tank the score).
 */
export function scoreNameSimilarity(a: string, b: string): number {
  const na = normalizeForMatch(a);
  const nb = normalizeForMatch(b);
  if (na.length === 0 || nb.length === 0) return 0;
  if (na === nb) return 1;
  return Math.max(diceCoefficient(na, nb), diceCoefficient(tokenSortNormalize(a), tokenSortNormalize(b)));
}

export type MatchConfidence = 'exact' | 'close' | 'none';

export interface NameMatchResult<T> {
  /** The chosen candidate when confidence is 'exact' or 'close'; null when 'none'. */
  candidate: T | null;
  score: number;
  confidence: MatchConfidence;
  /** True when the top two candidates are within `ambiguityDelta` — force a manual pick. */
  ambiguous: boolean;
}

export interface BestNameMatchOptions {
  /** Min score to treat as a safe, auto-applicable match. Default 0.8. */
  closeThreshold?: number;
  /** Min score to label the match "exact". Default 0.95. */
  exactThreshold?: number;
  /** If the top two candidates are closer than this, mark ambiguous. Default 0.05. */
  ambiguityDelta?: number;
}

/**
 * Best fuzzy match of `input` against `candidates`. Each candidate contributes one
 * or more comparison strings via `getStrings` (e.g. a teammate's name, email, and
 * email local-part); the candidate's score is the max over its strings.
 *
 * Confidence: exact ≥ exactThreshold, close ≥ closeThreshold (safe to auto-apply),
 * else none (prompt the user). When the top two candidates tie within
 * `ambiguityDelta`, the result is downgraded to `none` + `ambiguous` so the UI
 * forces a manual choice instead of guessing.
 */
export function bestNameMatch<T>(
  input: string,
  candidates: T[],
  getStrings: (candidate: T) => (string | null | undefined)[],
  opts?: BestNameMatchOptions,
): NameMatchResult<T> {
  const closeThreshold = opts?.closeThreshold ?? 0.8;
  const exactThreshold = opts?.exactThreshold ?? 0.95;
  const ambiguityDelta = opts?.ambiguityDelta ?? 0.05;

  const trimmed = input?.trim() ?? '';
  if (trimmed.length === 0 || candidates.length === 0) {
    return { candidate: null, score: 0, confidence: 'none', ambiguous: false };
  }

  const scored = candidates
    .map((candidate) => ({
      candidate,
      score: getStrings(candidate)
        .filter((s): s is string => s != null && s.length > 0)
        .reduce((max, s) => Math.max(max, scoreNameSimilarity(trimmed, s)), 0),
    }))
    .sort((a, b) => b.score - a.score);

  const top = scored[0];
  if (top == null || top.score < closeThreshold) {
    return { candidate: null, score: top?.score ?? 0, confidence: 'none', ambiguous: false };
  }

  const runnerUp = scored[1];
  const ambiguous = runnerUp != null && runnerUp.score >= closeThreshold && top.score - runnerUp.score < ambiguityDelta;
  const confidence: MatchConfidence = ambiguous ? 'none' : top.score >= exactThreshold ? 'exact' : 'close';

  return {
    candidate: confidence === 'none' ? null : top.candidate,
    score: top.score,
    confidence,
    ambiguous,
  };
}

export const contactNameV2 = (contact: Parameters<typeof contactNamesV2>[0]) => contactNamesV2(contact).join(' ');
export const contactNamesV2 = (contact: {
  id: string;
  primaryFields: { firstName?: SvrContactField; lastName?: SvrContactField; nameFull?: SvrContactField };
}) => {
  const firstName = contact.primaryFields.firstName?.data;
  const lastName = contact.primaryFields.lastName?.data;
  const nameFull = contact.primaryFields.nameFull?.data;

  return (isNotEmpty(firstName) && isNotEmpty(lastName) ? [firstName, lastName] : [nameFull]).filter(isNotEmpty);
};

export const contactNameFlat = (contact: { first_name: string | null; last_name: string | null }) =>
  [contact.first_name, contact.last_name].filter(isNotEmpty).join(' ');

/**
 * "Mr. John Doe" / "Dr. Jane Smith" style display. Prepends the honorific
 * when `shouldShowHonorific` returns true for the contact's age/threshold;
 * otherwise just returns the bare name.
 *
 * Defaults: pass `undefined` for `minAge` / `honorificAgeThreshold` and you'll
 * get the conservative "always show Dr., never show Mr./Ms./Mrs." behavior —
 * good for list views that don't have age data ready to hand.
 */
export const contactNameWithHonorific = (
  contact: { first_name: string | null; last_name: string | null; honorific_title?: string | null },
  minAge?: number | null,
  honorificAgeThreshold?: number | null,
): string => {
  const baseName = contactNameFlat(contact);
  const honorific = contact.honorific_title;
  if (!honorific || !shouldShowHonorific(honorific, minAge, honorificAgeThreshold)) return baseName;
  return baseName.length > 0 ? `${honorific} ${baseName}` : honorific;
};

export const objectKeys = <Obj extends Record<string, unknown>>(obj: Obj): (keyof Obj)[] => {
  return Object.keys(obj) as (keyof Obj)[];
};

export function insertStrIntoObject<T extends object, K extends keyof T>(obj: T, key: K, newValue: T[K]) {
  if (newValue != null && typeof newValue === 'string') {
    const trimmedNewValue = newValue.trim();
    if (trimmedNewValue.length > 0) {
      obj[key] = trimmedNewValue as unknown as T[K];
    }
  }
}

export function setUpdateObjectIfEmpty<T extends object, K extends keyof T>(
  obj: T,
  key: K,
  newValue: T[K],
  updateObj: Partial<T>,
) {
  const currentValue = obj[key];

  if (currentValue == null) {
    if (newValue == null) {
      return;
    }

    if (typeof newValue === 'string') {
      // value is a string insters it specially
      insertStrIntoObject(updateObj, key, newValue);
    } else {
      // value is null or undefined update
      updateObj[key] = newValue;
    }
    return;
  }

  if (typeof currentValue !== 'string') {
    // value is not a string, no not update
    // since the value is not null
    return;
  }

  // value is a string, check if empty
  const currentValueTrimmed = currentValue.trim();
  if (currentValueTrimmed.length <= 0) {
    // current value is an empty string, replace it
    insertStrIntoObject(updateObj, key, newValue);
    return;
  }

  // values is not null and not empty
  return;
}

export {
  calculateMinimumAgeFromWorkExperience,
  calculateMinimumAgeFromEducation,
  calculateMinimumAge,
} from './ageEstimate';

export {
  detectGender,
  detectGenderFromName,
  detectGenderFromHonorific,
  shouldShowHonorific,
  type GenderResult,
  type GenderEstimate,
  type GenderLanguage,
} from './genderDetection';

export {
  buildContactCardContact,
  type ContactCardContact,
  type ContactCardContactInput,
  type ContactCardTriggeredSignal,
} from './contactCard';

export {
  RSU_GRANTING_ORG_NAMES,
  EQUITY_GRANTING_ONLY_ORG_NAMES,
  isRsuGrantingOrgName,
  isEquityGrantingOrgName,
} from './equityGrantingOrgs';

export { buildTopSignal } from './topSignal';
export type { TopSignal, TopSignalInput } from './topSignal';

export {
  capitalize,
  chunk,
  groupBy,
  keyBy,
  mapValues,
  sortBy,
  orderBy,
  uniqBy,
  maxBy,
  minBy,
  max,
  min,
  omit,
  omitBy,
  pickBy,
  merge,
} from './collections';

export { formatAum } from './formatAum';
export {
  fitFromProbability,
  formatQualifyScore,
  formatQualifyReason,
  fitFromScore,
  QUALIFY_SCORE_ACCEPT_MIN,
  QUALIFY_SCORE_REJECT_MAX,
  QUALIFY_SCORE_WIDENED_ACCEPT_MIN,
  qualifyVerdictFromScore,
  qualifyScoreDetail,
  scoreFromClaudeFit,
  scoreFromJevProbability,
} from './qualifyFit';
export type { QualifyCriterionAnswer, QualifyVerdict } from './qualifyFit';
export { formatCompanySize } from './formatCompanySize';
export { formatEmployeeCountRange } from './formatEmployeeCountRange';
export { parseAum } from './parseAum';
